import { ReactElement, useEffect, useState } from 'react';
import {
  ApiResponse,
  CrudID,
  IBaseModel,
  IListFetchParams,
  MODULE,
  Paths,
  useList,
} from '../../common';
import { createRepository } from '../../dto/repositoryFactory';
import DropdownWithSearch from '@jobcost-monorepo/ui/molecules/DropdownWithSearch';
import { KeyValue } from '@jobcost-monorepo/utils';
import {
  generateKeyValue,
  generateKeyValueFromSingleElement,
} from '../../utils/helpers/helperKeyValues';
import { useFormContext } from 'react-hook-form';
import { AxiosError } from 'axios';
import { handleAxiosError } from '../../utils';
import React from 'react';

interface Props<T extends IBaseModel, Q extends IListFetchParams> {
  moduleName: MODULE;
  linkedField: Paths<T>;
  linkedFieldForDefaultValue: Paths<T>;
  label: string;
  width?: string;
  dependsOnFields?: Paths<T>[];
  dependsOnFieldsForSearch?: Paths<T>[];
  dependsOnMappedFieldsForSearch?: Record<string, Paths<Q>>[];
  dependsOnForcedFieldsForSearch?: Record<string, string | number | boolean>;
  forceDisabled?: boolean;
  onClearCallback?: () => void;
}

/**
 * DropdownCrud
 * @param moduleName - indica il nome del modulo API a cui far riferimento
 * @param linkedField - indica il nome del campo linkato del modello dati
 * @param linkedFieldForDefaultValue - indica il nome del campo linkato del modello dati per compilare il valore di
 *                                     default, è necessario per mostrare il contenuto del Dropdownn senza fare chiamate
 *                                     per recuperare i valori
 * @param label - la label sopra il dropdown
 * @param width - la classe da applicare al dropdown, non solo la width //TODO meglio cambiare nome
 * @param dependsOnFields - altri campi del modello dati che devono essere compilati perché il dropdown sia attivo
 * @param dependsOnFieldsForSearch - altri campi del modello dati che influiscono sulla ricerca come forcedParams, i valori
 *                                   vengono però ricavati dal context
 * @param dependsOnMappedFieldsForSearch - altri campi che influiscono sulla ricerca come forcedParams ma in questo caso
 *                                         sono in formato chiave valore, dove la chiave è un nome del campo che non ha a
 *                                         che fare con il modello dati, mentre il valore è pescato da un campo del modello dati
 * @param dependsOnForcedFieldsForSearch - altri campi che influiscono sulla ricerca come forcedParams ma in questo caso
 *                                         sono in formato chiave valore fissi, ossia non hanno a che fare con il modello dati
 *                                         e i valori sono fissi
 * @param forceDisabled - forza la disabilitazione del componente
 * @constructor
 */
const DropdownCrud = <T extends IBaseModel, Q extends IListFetchParams>({
  moduleName,
  linkedField,
  linkedFieldForDefaultValue,
  label,
  width = 'w-full',
  dependsOnFields,
  dependsOnFieldsForSearch,
  dependsOnMappedFieldsForSearch,
  dependsOnForcedFieldsForSearch,
  forceDisabled,
  onClearCallback,
}: Props<T, Q>): ReactElement => {
  const { getValues, watch } = useFormContext();
  const defaultValue = watch(linkedFieldForDefaultValue);
  const fieldsToWatch = watch(dependsOnFields?.map((el) => String(el)) ?? []);
  const isUsed = fieldsToWatch.every(
    (field) => field && field !== '' && field !== 0 && field !== null,
  );
  const isDisabled =
    dependsOnFields === undefined || dependsOnFields?.length === 0
      ? false
      : !isUsed;
  const dependsOnFieldsForSearchValues =
    dependsOnFieldsForSearch && getValues(dependsOnFieldsForSearch);

  const dependsFieldsParams =
    dependsOnFieldsForSearch &&
    dependsOnFieldsForSearchValues &&
    dependsOnFieldsForSearch.reduce(
      (acc, field, index) => {
        // @ts-ignore
        acc[field] = dependsOnFieldsForSearchValues[index] ?? undefined;
        return acc;
      },
      {} as Record<Paths<Q>, any>,
    );

  const getMappedFieldsValues = () => {
    const result: Record<string, any> = {};

    dependsOnMappedFieldsForSearch?.forEach((record) => {
      for (const key in record) {
        if (Object.prototype.hasOwnProperty.call(record, key)) {
          const path = record[key];
          const value = getValues(path as any);
          if (value !== undefined) {
            result[key] = value;
          }
        }
      }
    });

    return result as Record<Paths<Q>, any>;
  };

  const dependsOnMappedFieldsForSearchValues = getMappedFieldsValues();

  const repository = createRepository<T, Q>(moduleName);
  const { fetchDataList, params, dataList, loading } = useList<T, Q>({
    moduleName,
    repository,
    forcedParams: {
      ...dependsFieldsParams,
      ...dependsOnMappedFieldsForSearchValues,
      ...dependsOnForcedFieldsForSearch,
    },
  });
  const [items, setItems] = useState<KeyValue[]>([]);

  useEffect(() => {
    setItems(
      generateKeyValue(
        dataList,
        repository.keyValueMapFields.descriptionlinkedField ?? [],
      ),
    );
  }, [JSON.stringify(dataList)]);

  const handleSearch = async (search: string) => {
    await fetchDataList({
      ...params,
      ...dependsFieldsParams,
      skip: 0,
      take: 10,
      search: search,
    } as Q);
  };

  const handleGetSingleItem = async (id: CrudID) => {
    return await repository
      .get(id)
      .then((response: ApiResponse<T>) => {
        return generateKeyValueFromSingleElement(
          response,
          repository.keyValueMapFields.descriptionlinkedField ?? [],
        );
      })
      .catch((err: AxiosError) => handleAxiosError(err));
  };

  return (
    <DropdownWithSearch
      errorName={linkedField}
      items={items}
      name={linkedField}
      label={label}
      onChangeSearch={handleSearch}
      width={width}
      defaultValue={
        linkedFieldForDefaultValue && defaultValue
          ? String(defaultValue)
          : undefined
      }
      disabled={forceDisabled || isDisabled}
      handleGetSingleItem={handleGetSingleItem}
      loading={loading}
      onClearCallback={onClearCallback}
    />
  );
};

export default DropdownCrud;
